import { useEffect, MouseEvent, useState, ChangeEvent } from 'react';
import { SerializedError } from '@reduxjs/toolkit';
import { useForm, useFieldArray } from 'react-hook-form';
import { FaPlus, FaTrashAlt } from 'react-icons/fa';
import _ from 'lodash';

import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { push, useRouteMatch } from '../../app/route';
import {
  selectCurrentTenantId,
} from '../user/userSlice';
import {
  getTenantAction,
  selectGetTenantResult,
  selectUpdateTenantResult,
  updateTenantAction,
  tenant_field_options,
  getTenantDetailUrl,
} from './tenantSlice';
import { Tenant, TenantProvider } from '../../types';
import { TenantProviderModal, TenantProviderModalProps } from './TenantProviderModal';

type TenantForm = Required<Tenant>;
const initialState: TenantForm = {
  tenant_id: '',
  name: '',
  title: '',
  description: '',
  providers: [],
  state: 'enabled',
  username: {
    can_signup: false,
    can_login: false,
  },
  fqdn: '',
  signup_role_id: '',
  custom_domains: [],
  auto_link_email: false,
};

type PathParams = {
  tenant_id: string;
};

type ProviderModalProps = Pick<TenantProviderModalProps, 'show'|'init'|'mode'>;

export function TenantEditPage() {
  const dispatch = useAppDispatch();
  const match = useRouteMatch<PathParams>();
  const get_result = useAppSelector(selectGetTenantResult);
  const update_result = useAppSelector(selectUpdateTenantResult);
  const tenant_id = useAppSelector(selectCurrentTenantId);
  const [api_error, setApiError] = useState<SerializedError|undefined>();
  const [provider_modal_props, setProviderModalProps] = useState<ProviderModalProps>({ show: false, mode: 'edit' });
  const [custom_domain_fqdn, setCustomDomainFqdn] = useState('');
  const { handleSubmit, control, register, watch, reset, formState: { errors } } = useForm({
    defaultValues: initialState,
  });
  const { fields: providers, update, append, remove } = useFieldArray({
    control,
    name: 'providers',
  });
  const { fields: custom_domains, append: appendCustomDomain, remove: removeCustomDomain } = useFieldArray({
    control,
    name: 'custom_domains',
  });

  const fqdn = watch('fqdn');

  useEffect(() => {
    if (tenant_id !== match.params.tenant_id) {
      dispatch(push(getTenantDetailUrl(tenant_id, 'edit')));
    }
  }, [tenant_id, dispatch, match.params.tenant_id]);

  useEffect(() => {
    dispatch(getTenantAction({ tenant_id }));
  }, [tenant_id, dispatch]);

  useEffect(() => {
    if (get_result) {
      if (getTenantAction.fulfilled.match(get_result)) {
        reset({
          ...initialState,
          ...get_result.payload,
        });
      } else {
        const error = get_result.error;
        setApiError(error);
      }
    }
  }, [get_result, reset]);

  useEffect(() => {
    if (update_result) {
      if (updateTenantAction.fulfilled.match(update_result)) {
        dispatch(push(getTenantDetailUrl(tenant_id, 'view')));
      } else {
        setApiError(update_result.error);
        const subscription = watch(() => {
          setApiError(undefined);
          subscription.unsubscribe();
        });
      }
    }
  }, [update_result, watch, dispatch, tenant_id]);

  const handleUpdate = (draft: TenantForm) => {
    dispatch(updateTenantAction({
      tenant_id,
      body: {
        ...draft,
      },
    }));
  };
  const handleCancel = (event: MouseEvent) => {
    event.preventDefault();
    dispatch(push(getTenantDetailUrl(tenant_id, 'view')));
  };
  const handleAddProviderModal = (e: MouseEvent, provider?: TenantProvider) => {
    e.preventDefault();
    setProviderModalProps({ show: true, init: provider, mode: 'new' });
  };

  const handleChangeCustomDomain = (event: ChangeEvent<HTMLInputElement>) => {
    setCustomDomainFqdn(event.target.value);
  };

  const handleAddCustomDomain = () => {
    if (!custom_domains.find(custom_domain => custom_domain.fqdn === custom_domain_fqdn)) {
      appendCustomDomain({ fqdn: custom_domain_fqdn, is_verified: true });
      setCustomDomainFqdn('');
    }
  };

  const handleRemoveCustomDomain = (event: MouseEvent, input: { fqdn: string; }) => {
    event.preventDefault();
    let index = custom_domains.findIndex(custom_domain => custom_domain.fqdn === input.fqdn);
    removeCustomDomain(index);
  };

  const handleEditProviderModal = (e: MouseEvent, provider?: TenantProvider) => {
    e.preventDefault();
    setProviderModalProps({ show: true, init: provider, mode: 'edit' });
  };
  const handleRemoveProviderModal = (provider: TenantProvider) => {
    let idx = _.findIndex(providers, ({ name }) => name === provider.name);
    remove(idx);
    setProviderModalProps({ show: false, mode: 'edit' });
  };
  const handleCloseProviderModal = (provider?: TenantProvider) => {
    if (provider) {
      let idx = _.findIndex(providers, ({ name }) => name === provider.name);
      if (idx === -1) {
        append(provider);
      } else {
        update(idx, provider);
      }
    }
    setProviderModalProps({ show: false, mode: 'edit' });
  };
  return (
    <div className="container">
      <div className="d-flex justify-content-between py-4">
        <h1>Tenant Info</h1>
      </div>
      <form onSubmit={handleSubmit(handleUpdate)}>
        <fieldset>
          <div className="mb-3 row">
            <label htmlFor="tenant_tenant_id" className="col-sm-3 col-form-label">TenantId</label>
            <div className="col-sm-6">
              <input disabled type="text" id="tenant_tenant_id" className="form-control-plaintext"
                { ...register('tenant_id') }
              />
            </div>
          </div>
          <div className="mb-3 row">
            <label htmlFor="tenant_title" className="col-sm-3 col-form-label">Title</label>
            <div className="col-sm-6">
              <input type="text" id="tenant_title" className="form-control" autoComplete="off"
                { ...register('title', tenant_field_options.title) }
              />
            </div>
          </div>
          <div className="mb-3 row">
            <label htmlFor="tenant_name" className="col-sm-3 col-form-label">Name</label>
            <div className="col-sm-6">
              <input disabled type="text" id="tenant_name" className="form-control-plaintext"
                { ...register('name') }
              />
            </div>
          </div>
          <div className="mb-3 row">
            <label htmlFor="tenant_description" className="col-sm-3 col-form-label">Description</label>
            <div className="col-sm-6">
              <textarea id="tenant_description" className="form-control"
                { ...register('description', tenant_field_options.description) }
              >
              </textarea>
            </div>
            <span className="col-sm-3 text-danger py-1">{errors.description?.message}</span>
          </div>
          <div className="mb-3 row">
            <label htmlFor="tenant_description" className="col-sm-3 col-form-label">FQDN</label>
            <div className="col-sm-6">
              <span className="form-control-plaintext">{fqdn}</span>
            </div>
          </div>
          <div className="mb-3 row">
            <label htmlFor="username_can_login" className="col-sm-3 col-form-label">Username Can Login</label>
            <div className="mt-2 col-sm-6">
              <div className="col-sm-6">
                <input id="username_can_login" type="checkbox" className="form-check-input"
                  { ...register('username.can_login', tenant_field_options.username?.can_login) }
                />
              </div>
            </div>
            <span className="col-sm-3 text-danger py-1">{errors.username?.can_login?.message}</span>
          </div>
          <div className="mb-3 row">
            <label htmlFor="username_can_signup" className="col-sm-3 col-form-label">Username Can Signup</label>
            <div className="mt-2 col-sm-6">
              <div className="col-sm-6">
                <input id="username_can_signup" type="checkbox" className="form-check-input"
                  { ...register('username.can_signup', tenant_field_options.username?.can_signup) }
                />
              </div>
            </div>
            <span className="col-sm-3 text-danger py-1">{errors.username?.can_signup?.message}</span>
          </div>
          <div className="mb-3 row">
            <label htmlFor="state" className="col-sm-3 col-form-label">Providers</label>
            <div className="mt-2 col-sm-6">
              {providers.map(provider => (
                <a key={provider.name} className="me-1" href="/#" role="button" onClick={(e) => handleEditProviderModal(e, provider)}>
                  <span className="badge bg-success px-2 py-1">{provider.name}</span>
                </a>
              ))}
              <a role="button" href="/#" className="mx-1" onClick={handleAddProviderModal}>
                <FaPlus/>
              </a>
            </div>
          </div>
          <div className="mb-3 row">
            <label htmlFor="state" className="col-sm-3 col-form-label">Custom Domians</label>
            <div className="mt-2 col-sm-6">
              {custom_domains.map(custom_domain => (
                <div key={custom_domain.fqdn}
                  className="d-flex justify-content-between align-items-center px-1 mb-2 text-primary">
                  <span className="px-2 py-1">{custom_domain.fqdn}</span>
                  <a href="/#" role="button" onClick={(e) => handleRemoveCustomDomain(e, custom_domain)}>
                    <FaTrashAlt/>
                  </a>
                </div>
              ))}
              <div className="input-group mb-3">
                <input type="text" className="form-control" placeholder="Custom domain" name="custom_domain"
                  onChange={handleChangeCustomDomain} value={custom_domain_fqdn}
                />
                <button className="btn btn-outline-info" type="button" id="custom_domain" onClick={handleAddCustomDomain}>
                  <FaPlus/>
                </button>
              </div>
            </div>
          </div>
          <div className="mb-3 row">
            <label htmlFor="signup_role_id" className="col-sm-3 col-form-label">Sign Up RoleId</label>
            <div className="col-sm-6">
              <input id="signup_role_id" className="form-control" autoComplete="off"
                { ...register('signup_role_id', tenant_field_options.signup_role_id) }
              />
            </div>
            <span className="col-sm-3 text-danger py-1">{errors.signup_role_id?.message}</span>
          </div>
          <div className="mb-3 row">
            <label htmlFor="username_can_login" className="col-sm-3 col-form-label">Auto Link Email</label>
            <div className="mt-2 col-sm-6">
              <div className="col-sm-6">
                <input id="username_can_login" type="checkbox" className="form-check-input"
                  { ...register('auto_link_email', tenant_field_options.auto_link_email) }
                />
              </div>
            </div>
            <span className="col-sm-3 text-danger py-1">{errors.username?.can_login?.message}</span>
          </div>
          <div className="mb-3 row">
            <label htmlFor="state" className="col-sm-3 col-form-label">State</label>
            <div className="col-sm-6">
              <select id="state" className="form-select"
                { ...register('state') }
              >
                <option value="enabled">Enabled</option>
                <option value="disabled">Disabled</option>
              </select>
            </div>
          </div>
          <div className="d-flex">
            <span>{api_error?.message ?? api_error?.name}</span>
          </div>
          <div className="d-flex my-3 justify-content-center">
            <button type="submit" className="btn btn-primary mx-3">Update</button>
            <button className="btn btn-light mx-1" onClick={(e) => handleCancel(e)}>Cancel</button>
          </div>
        </fieldset>
      </form>
      <TenantProviderModal
        {...provider_modal_props}
        onClose={handleCloseProviderModal}
        onRemove={handleRemoveProviderModal}
      />
    </div>
  );
}
